import { useEffect, useMemo, useState } from "react";
import "./App.css";
import "./Login.css";
import { AppointmentRules, ServiceCatalog } from "./ServiceCatalog";
import { CoverageSchedule } from "./CoverageSchedule";
import { HolidaySchedule } from "./HolidaySchedule";
import { CallUpdates, CallerRules, ForwardingSetup } from "./RoutingControls";
import { CallLogUpload } from "./CallLogUpload";
import { OperationalPolicies } from "./OperationalPolicies";

const API =
  window.location.hostname === "localhost"
    ? "http://localhost:8787"
    : "https://ams-onboarding-api.biglexlost.workers.dev";
const OFFER = "RevEdge Auto [Lite Edition]";
const EMPTY = {
  business: {},
  coverage: {},
  booking: {},
  visibility: {},
  authorization: {},
};
const SECTIONS = [
  [
    "business",
    "01 • Business",
    "Shop and launch authority",
    "Confirm the exact location and people authorized to launch.",
    [
      ["legalName", "Legal business name", "text", 0],
      ["dbaName", "Public shop / DBA name", "text", 1],
      [
        "coveredLocation",
        "Covered guarantee location",
        "textarea",
        1,
        "Street address, city, state, ZIP. One location per guarantee.",
      ],
      [
        "timezone",
        "Local IANA timezone",
        "text",
        1,
        "Example: America/Chicago",
      ],
      ["mainPhone", "Primary customer-facing phone", "tel", 1],
      [
        "phoneSystem",
        "Current phone system",
        "select",
        0,
        "",
        [
          "Traditional landline",
          "Mobile phone",
          "Google Voice",
          "RingCentral",
          "Nextiva",
          "Dialpad",
          "8x8",
          "Vonage",
          "GoTo Connect",
          "Grasshopper",
          "Weave",
          "Other",
        ],
      ],
      [
        "phoneSystemOther",
        "Other phone system",
        "text",
        0,
        "Name the phone system you use.",
      ],
      [
        "launchOwner",
        "Client launch owner",
        "text",
        1,
        "Name, title, mobile, email",
      ],
      [
        "goLiveApprover",
        "Authorized go-live approver",
        "text",
        1,
        "Name, title, mobile, email",
      ],
      [
        "guaranteeTier",
        "Guarantee program tier",
        "select",
        1,
        "Select the guarantee being evaluated. $50K: 90 eligible missed calls/30 days → 54 bookings → 27 completed repairs/month → 81 in 90 days → ~$617 average verified repair order. $100K: 150 → 90 → 45/month → 135 in 90 days → ~$741 average verified repair order.",
        ["No guarantee", "$50K guarantee", "$100K guarantee"],
      ],
    ],
  ],
  [
    "coverage",
    "02 • Routing",
    "Coverage, callbacks, and escalation",
    "Define when JAIMES handles calls and how work reaches your team.",
    [
      [
        "callbackHours",
        "Callback coverage hours",
        "textarea",
        1,
        "List each day and local start/end time.",
      ],
      ["closures", "Holiday and closure process", "textarea", 0],
      [
        "primaryCallback",
        "Primary callback phone",
        "tel",
        1,
        "Use the number that should receive callback requests.",
      ],
      [
        "backupCallback",
        "Backup callback phone",
        "tel",
        0,
        "Optional backup number for callback requests.",
      ],
      [
        "escalationRules",
        "Immediate escalation rules",
        "textarea",
        1,
        "Safety, stranded driver, towing, upset caller, or other approved rule.",
      ],
      [
        "escalationDestination",
        "Escalation destination and fallback",
        "textarea",
        1,
      ],
      [
        "callbackExpectation",
        "Callback response target",
        "select",
        0,
        "",
        [
          "Within 30 minutes",
          "Within 1 hour",
          "Within 2 hours",
          "Within 24 business hours",
        ],
      ],
      [
        "forwardingPlan",
        "Forwarding and voicemail setup",
        "textarea",
        1,
        "Include overflow timing, after-hours, weekends, voicemail, and other answering providers.",
      ],
    ],
  ],
  [
    "booking",
    "03 • Operating truth",
    "Booking and service rules",
    "JAIMES-Lite books only within approved rules and never diagnoses vehicles.",
    [
      [
        "calendar",
        "Scheduling contact phone",
        "tel",
        1,
        "Number to call when an appointment needs scheduling help.",
      ],
      [
        "slotRules",
        "Appointment slot rules",
        "textarea",
        0,
        "Lead time, same-day limits, duration, buffers, blocked times, and horizon.",
      ],
      [
        "directBookServices",
        "Services JAIMES may book directly",
        "textarea",
        0,
      ],
      ["callbackServices", "Services requiring a callback", "textarea", 0],
      [
        "customerPay",
        "Accepting new customer-pay work",
        "select",
        1,
        "",
        ["Yes", "No", "Conditional"],
      ],
      ["serviceArea", "Service area and vehicle restrictions", "textarea", 1],
      [
        "approvedLanguage",
        "Approved service and caller wording",
        "textarea",
        1,
      ],
      [
        "safetyLanguage",
        "Safety, towing, and non-service wording",
        "textarea",
        0,
      ],
      ["valuationRules", "Approved service-value mapping", "textarea", 0],
      ["valuationOwner", "Service-value approval owner", "text", 0],
    ],
  ],
  [
    "visibility",
    "04 • Access",
    "Feed, alerts, privacy, and training",
    "Lite exposes concise outcomes only, never transcripts or recordings.",
    [
      [
        "feedRecipients",
        "Live Call Feed recipients",
        "textarea",
        1,
        "Names and emails for the private Discord feed.",
      ],
      ["textAlerts", "Text Alerts purchased", "select", 1, "", ["No", "Yes"]],
      [
        "textRecipient",
        "Text Alerts phone",
        "tel",
        0,
        "Required only when Text Alerts are purchased.",
      ],
      ["bookedAlertOwner", "Booked-appointment alert owner", "text", 0],
      ["dataOwner", "Client data owner", "text", 1],
      ["privacyContact", "Deletion and privacy request contact", "text", 1],
      ["training", "Training owner and attendees", "textarea", 0],
      ["changeContact", "Authorized change-request contact", "text", 0],
    ],
  ],
];
const TESTS = [
  [
    "booking",
    "Booking",
    "Controlled appointment appears on the approved calendar.",
  ],
  [
    "callback",
    "Callback",
    "Callback reaches the primary recipient and remains visible.",
  ],
  ["afterHours", "After-hours", "Test follows next-covered-period handling."],
  ["escalation", "Escalation", "Approved destination and fallback work."],
  [
    "feed",
    "Feed and alerts",
    "Private feed works; Text Alerts work only if purchased.",
  ],
  ["privacy", "Summary privacy", "No transcript or recording is exposed."],
  ["rollback", "Rollback", "The shop knows how to stop forwarding."],
];
const GATES = [
  "Commercial and guarantee eligibility handoff complete",
  "Signed service agreement and covered location recorded",
  "Client intake and approvals complete",
  "RevEdge Auto configuration, IDs, permissions, credentials, and mappings verified",
  "No-transcript and no-recording controls verified",
  "24-business-hour callback behavior deployed and tested",
  "Full controlled acceptance matrix passed",
  "Forwarding and rollback confirmed in production",
  "72-hour hypercare exit approved",
];
async function call(path, { token, method = "GET", body } = {}) {
  const h = { "Content-Type": "application/json" };
  if (token) h.Authorization = `Bearer ${token}`;
  return fetch(API + path, {
    method,
    headers: h,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}
const norm = (v) => ({ ...EMPTY, ...(v || {}) });
function completion(data) {
  const d = norm(data),
    req = SECTIONS.flatMap((s) =>
      s[4].filter((f) => f[3]).map((f) => [s[0], f[0]]),
    );
  if (d.business?.guaranteeTier && d.business.guaranteeTier !== "No guarantee") req.push(["business", "callLogFile"]);
  const
    filled = req.filter(([s, f]) => String(d[s]?.[f] || "").trim()).length;
  return {
    filled,
    total: req.length,
    percent: Math.round((filled / req.length) * 100),
  };
}
function Logo() {
  return (
    <div className="brand">
      <img src="/revedge-auto-logo.png" alt="RevEdge Auto" />
      <div>
        <b>RevEdge Auto</b>
        <span>Lite Edition • Powered by JAIMES</span>
      </div>
    </div>
  );
}
function Login({ id, onAuth }) {
  const [p, setP] = useState(""),
    [e, setE] = useState(""),
    [busy, setBusy] = useState(false),
    admin = !id;
  async function submit(x) {
    x.preventDefault();
    setBusy(true);
    setE("");
    try {
      const r = await call("/api/auth", {
          method: "POST",
          body: admin ? { role: "admin", pin: p } : { id, pin: p },
        }),
        d = await r.json();
      if (!r.ok) throw Error(d.error || "Unable to sign in");
      onAuth(d);
    } catch (x) {
      setE(
        x.message === "Failed to fetch"
          ? "The onboarding service is unavailable."
          : x.message,
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="login">
      <section>
        <Logo />
        <p className="kicker">
          {admin ? "AMS launch control" : "Shop Onboarding Portal"}
        </p>
        <h1>
          {admin ? "Manage shop onboarding." : "Finish your launch setup."}
        </h1>
        <p>
          {admin
            ? "Review intake, clear launch gates, and authorize production."
            : "Confirm the operating rules JAIMES-Lite will follow before calls go live."}
        </p>
        <form onSubmit={submit}>
          <label>Access PIN</label>
          <input
            className="pin"
            inputMode="numeric"
            value={p}
            onChange={(x) =>
              setP(x.target.value.replace(/\D/g, "").slice(0, 10))
            }
          />
          {e && <small className="error">{e}</small>}
          <button disabled={!p || busy}>
            {busy ? "Checking…" : "Open workspace"}
          </button>
        </form>
      </section>
    </main>
  );
}
const TIMEZONES = [
  ["America/New_York", "Eastern Time"],
  ["America/Chicago", "Central Time"],
  ["America/Denver", "Mountain Time"],
  ["America/Phoenix", "Arizona Time"],
  ["America/Los_Angeles", "Pacific Time"],
  ["America/Anchorage", "Alaska Time"],
  ["Pacific/Honolulu", "Hawaii Time"],
];
const PHONE_FIELDS = new Set([
  "mainPhone",
  "primaryCallback",
  "backupCallback",
  "calendar",
  "textRecipient",
]);
const formatPhone = (value) => {
  const d = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 10);
  return d.length < 4
    ? d
    : d.length < 7
      ? `(${d.slice(0, 3)}) ${d.slice(3)}`
      : `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
function Field({ f, value, set }) {
  let [n, l, t, required, hint, opts] = f;
  const timezone = n === "timezone",
    phone = PHONE_FIELDS.has(n);
  return (
    <div className={t === "textarea" ? "field wide" : "field"}>
      <label>
        {l}
        {Boolean(required) && <em>Required</em>}
      </label>
      {t === "textarea" ? (
        <textarea
          rows="3"
          value={value || ""}
          onChange={(e) => set(e.target.value)}
        />
      ) : t === "select" || timezone ? (
        <select value={value || ""} onChange={(e) => set(e.target.value)}>
          <option value="">Select…</option>
          {(timezone ? TIMEZONES : opts.map((o) => [o, o])).map(([v, text]) => (
            <option value={v} key={v}>
              {text}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={phone ? "tel" : t}
          inputMode={phone ? "numeric" : undefined}
          maxLength={phone ? 14 : undefined}
          value={phone ? formatPhone(value) : value || ""}
          placeholder={phone ? "(555) 555-5555" : undefined}
          onChange={(e) =>
            set(phone ? formatPhone(e.target.value) : e.target.value)
          }
        />
      )}{" "}
      {hint && <small>{hint}</small>}
    </div>
  );
}
function Intake({ draft, setDraft, clientId, token, admin }) {
  const c = useMemo(() => completion(draft), [draft]);
  function change(s, f, v) {
    setDraft((d) => ({ ...d, [s]: { ...d[s], [f]: v } }));
  }
  return (
    <>
      {SECTIONS.map((s) => {
        const fields =
          s[0] === "booking"
            ? s[4].filter(
                (f) =>
                  ![
                    "directBookServices",
                    "callbackServices",
                    "slotRules",
                    "approvedLanguage",
                    "safetyLanguage",
                    "valuationRules",
                  ].includes(f[0]),
              )
            : s[0] === "coverage"
              ? s[4].filter(
                  (f) =>
                    !["callbackHours", "closures", "forwardingPlan"].includes(
                      f[0],
                    ),
                )
              : s[0] === "visibility"
                ? s[4].filter((f) => f[0] !== "feedRecipients")
                : s[4];
        return (
          <section className="panel section" key={s[0]}>
          <header>
              <p>{s[1]}</p>
              <h2>{s[2]}</h2>
              <span>{s[3]}</span>
          </header>
          {s[0] === "business" && (
            <CallLogUpload clientId={clientId} token={token} draft={draft} setDraft={setDraft} admin={admin} />
          )}
            {s[0] === "coverage" && (
              <>
                <CoverageSchedule draft={draft} setDraft={setDraft} />
                <HolidaySchedule draft={draft} setDraft={setDraft} />
                <ForwardingSetup draft={draft} setDraft={setDraft} />
              </>
            )}{" "}
            {s[0] === "booking" && (
              <>
                <ServiceCatalog draft={draft} setDraft={setDraft} />
                <OperationalPolicies draft={draft} setDraft={setDraft} />
                <AppointmentRules draft={draft} setDraft={setDraft} />
                <CallerRules draft={draft} setDraft={setDraft} />
              </>
            )}{" "}
            {s[0] === "visibility" && (
              <CallUpdates draft={draft} setDraft={setDraft} />
            )}
            <div className="grid">
              {fields
                .filter(
                  (f) =>
                    f[0] !== "phoneSystemOther" ||
                    draft.business?.phoneSystem === "Other",
                )
                .map((f) => (
                  <Field
                    key={f[0]}
                    f={f}
                    value={draft[s[0]]?.[f[0]]}
                    set={(v) => change(s[0], f[0], v)}
                  />
                ))}
            </div>
          </section>
        );
      })}
      <Authorization draft={draft} setDraft={setDraft} />
      <input type="hidden" value={c.percent} />
    </>
  );
}
function Authorization({ draft, setDraft }) {
  const a = draft.authorization || {},
    tests = a.tests || {},
    set = (k, v) =>
      setDraft((d) => ({ ...d, authorization: { ...a, [k]: v } }));
  return (
    <section className="panel section">
      <header>
        <p>05 • Authorization</p>
        <h2>Controlled tests and go-live approval</h2>
        <span>
          Production forwarding stays blocked until every required gate passes.
        </span>
      </header>
      <div className="tests">
        {TESTS.map(([k, l, d]) => (
          <label key={k}>
            <input
              type="checkbox"
              checked={!!tests[k]}
              onChange={(e) =>
                set("tests", { ...tests, [k]: e.target.checked })
              }
            />
            <span>
              <b>{l}</b>
              <small>{d}</small>
            </span>
          </label>
        ))}
      </div>
      <div className="grid">
        {[
          ["testDate", "Controlled test date and evidence", "text", 1],
          ["forwardingWindow", "Production forwarding window", "text", 1],
          ["rollbackAuthority", "Primary rollback authority", "text", 1],
          ["signer", "Authorized signer and title", "text", 1],
        ].map((f) => (
          <Field key={f[0]} f={f} value={a[f[0]]} set={(v) => set(f[0], v)} />
        ))}
      </div>
      <label className="accept">
        <input
          type="checkbox"
          checked={!!a.accepted}
          onChange={(e) => set("accepted", e.target.checked)}
        />
        <span>
          I confirm this information is accurate and authorize forwarding only
          after the listed tests and AMS launch gates pass.
        </span>
      </label>
    </section>
  );
}
function Checklist({ client, admin, onChange }) {
  const list = client.launchChecklist?.length
    ? client.launchChecklist
    : GATES.map((label, i) => ({
        id: i + 1,
        label,
        status: "pending",
        evidence: "",
      }));
  const edit = (i, p) =>
    onChange(list.map((x, n) => (n === i ? { ...x, ...p } : x)));
  return (
    <section className="panel checklist">
      <header>
        <p className="kicker">Launch control</p>
        <h2>{admin ? "AMS implementation checklist" : "Launch readiness"}</h2>
        <span>
          {admin
            ? "A blank evidence field is a failed gate."
            : "AMS clears these controls before your main line forwards."}
        </span>
      </header>
      {list.map((g, i) => (
        <div className="gate" key={g.id}>
          <i className={g.status} />
          <div>
            <b>{g.label}</b>
            {admin && (
              <input
                placeholder="Evidence reference or exception"
                value={g.evidence || ""}
                onChange={(e) => edit(i, { evidence: e.target.value })}
              />
            )}
          </div>
          {admin ? (
            <select
              value={g.status}
              onChange={(e) => edit(i, { status: e.target.value })}
            >
              <option value="pending">Pending</option>
              <option value="in_progress">In progress</option>
              <option value="complete">Pass</option>
              <option value="blocked">Blocked</option>
            </select>
          ) : (
            <span>{g.status.replace("_", " ")}</span>
          )}
        </div>
      ))}
    </section>
  );
}
function Workspace({ initial, token, admin, onExit }) {
  const [client, setClient] = useState({
      ...initial,
      onboardingData: norm(initial.onboardingData),
    }),
    [draft, setDraft] = useState(norm(initial.onboardingData)),
    [tab, setTab] = useState("overview"),
    [msg, setMsg] = useState(""),
    [busy, setBusy] = useState(false),
    c = useMemo(() => completion(draft), [draft]),
    testMode = !admin && client.id === "revedgetest";
  async function save(submit = false) {
    setBusy(true);
    setMsg("");
    try {
      const path = admin
          ? `/api/admin/clients/${client.id}`
          : `/api/client/${client.id}/onboarding`,
        body = admin
          ? {
              onboardingData: draft,
              onboardingStatus: submit ? "submitted" : client.onboardingStatus,
              launchChecklist: client.launchChecklist,
            }
          : { onboardingData: draft, submit },
        r = await call(path, { token, method: "PUT", body }),
        d = await r.json();
      if (!r.ok) throw Error(d.error || "Save failed");
      setClient(d);
      setDraft(norm(d.onboardingData));
      setMsg(submit ? "Submitted to AMS." : "Saved.");
      if (submit) setTab("overview");
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  }
  const passed = (client.launchChecklist || []).filter(
    (g) => g.status === "complete",
  ).length;
  return (
    <div className="app">
      <header className="top">
        <Logo />
        <div>
          <span>
            {client.businessName || draft.business?.dbaName || "New shop"}
          </span>
          {admin && <b>Admin</b>}
          <button onClick={onExit}>{admin ? "All shops" : "Sign out"}</button>
        </div>
      </header>
      <nav>
        {["overview", "intake", "checklist"].map((x) => (
          <button
            className={tab === x ? "active" : ""}
            onClick={() => setTab(x)}
            key={x}
          >
            {x === "intake" ? "Shop setup" : x}
          </button>
        ))}
      </nav>
      <main className="workspace">
        <div className="heading">
          <p className="kicker">
            {admin
              ? "Client implementation"
              : `Welcome, ${client.ownerName || "shop owner"}`}
          </p>
          <h1>
            {tab === "overview"
              ? c.percent === 0
                ? "Start your launch setup."
                : "Finish your launch setup."
              : tab === "intake"
                ? "Define the operating truth."
                : "Clear every launch gate."}
          </h1>
        </div>
        {tab === "overview" && (
          <>
            <div className="cards">
              <section className="panel metric">
                <p>YOUR ONBOARDING</p>
                <strong>{c.percent}%</strong>
                <span>
                  {c.filled} of {c.total} required details
                </span>
                <progress value={c.percent} max="100" />
                <button onClick={() => setTab("intake")}>
                  Continue onboarding
                </button>
              </section>
              <section className="panel offer">
                <p>YOUR PLAN</p>
                <h2>{OFFER}</h2>
                <b>$497/month</b>
                <span>200 AI minutes included • $0.30/additional minute</span>
                <small>
                  Live Call Feed included. Text Alerts optional at $19.95/month.
                </small>
              </section>
              <section className="panel offer">
                <p>LAUNCH STATUS</p>
                <b
                  className={`status status-${(client.onboardingStatus || "draft").replace("_", "-")}`}
                >
                  {(client.onboardingStatus || "draft").replace("_", " ")}
                </b>
                <h2
                  className={`gate-count ${passed ? "gate-ready" : "gate-pending"}`}
                >
                  {passed
                    ? `${passed} of ${GATES.length} gates cleared`
                    : "Launch gates pending"}
                </h2>
                <small>
                  {client.statusMessage ||
                    "AMS will review your information and coordinate testing."}
                </small>
              </section>
            </div>
            <aside>
              <b>Lite scope:</b> JAIMES-Lite handles approved overflow and
              after-hours calls, books qualifying appointments, and routes human
              follow-up. It does not diagnose vehicles or expose transcripts or
              recordings.
            </aside>
          </>
        )}
        {tab === "intake" && (
          <div className="stack">
            <Intake draft={draft} setDraft={setDraft} clientId={client.id} token={token} admin={admin} />
            <div className="save">
              <span>{msg || `${c.percent}% complete`}</span>
              <button className="secondary" onClick={() => save()}>
                Save draft
              </button>
              <button
                onClick={() => save(true)}
                disabled={
                  busy ||
                  (!testMode &&
                    (c.percent < 100 || !draft.authorization?.accepted))
                }
              >
                {testMode ? "Submit test onboarding" : "Submit to AMS"}
              </button>
            </div>
          </div>
        )}
        {tab === "checklist" && (
          <>
            <Checklist
              client={client}
              admin={admin}
              onChange={(l) => setClient((x) => ({ ...x, launchChecklist: l }))}
            />
            {admin && (
              <div className="save">
                <span>{msg}</span>
                <button onClick={() => save()}>Save launch controls</button>
              </div>
            )}
          </>
        )}
      </main>
      <footer>Advantage Marketing Systems • RevEdge Auto</footer>
    </div>
  );
}
function Admin({ token, logout }) {
  const [clients, setClients] = useState([]),
    [selected, setSelected] = useState(),
    [notice, setNotice] = useState(""),
    [form, setForm] = useState({ businessName: "", ownerName: "", email: "" }),
    [sending, setSending] = useState(false);
  async function load() {
    const r = await call("/api/admin/clients", { token }),
      d = await r.json();
    if (r.ok) setClients(d);
    else setNotice(d.error);
  }
  useEffect(() => {
    load();
  }, []);
  async function add(event) {
    event.preventDefault();
    setSending(true);
    setNotice("");
    try {
      const r = await call("/api/admin/clients", {
          token,
          method: "POST",
          body: form,
        }),
        d = await r.json();
      if (!r.ok) throw Error(d.error || "Unable to create shop");
      setForm({ businessName: "", ownerName: "", email: "" });
      setNotice(`Onboarding invite sent to ${form.email}.`);
      load();
    } catch (error) {
      setNotice(error.message);
    } finally {
      setSending(false);
    }
  }
  async function resend(id) {
    setSending(true);
    setNotice("");
    try {
      const r = await call(`/api/admin/clients/${id}/send-invite`, {
          token,
          method: "POST",
        }),
        d = await r.json();
      if (!r.ok) throw Error(d.error || "Unable to resend invite");
      setNotice("A fresh temporary PIN and onboarding link were emailed.");
      load();
    } catch (error) {
      setNotice(error.message);
    } finally {
      setSending(false);
    }
  }
  if (selected)
    return (
      <Workspace
        initial={selected}
        token={token}
        admin
        onExit={() => {
          setSelected();
          load();
        }}
      />
    );
  return (
    <div className="app">
      <header className="top">
        <Logo />
        <button onClick={logout}>Sign out</button>
      </header>
      <main className="workspace">
        <div className="adminhead">
          <div className="heading">
            <p className="kicker">Launch control</p>
            <h1>RevEdge Auto Shops</h1>
          </div>
        </div>
        <form className="panel invite" onSubmit={add}>
          <div>
            <p className="kicker">New shop</p>
            <h2>Send onboarding invite</h2>
            <span>
              Creates a private portal and emails a one-time access PIN from
              Advantage Marketing Systems.
            </span>
          </div>
          <label>
            Public shop name
            <input
              value={form.businessName}
              onChange={(event) =>
                setForm((x) => ({ ...x, businessName: event.target.value }))
              }
              placeholder="Harborview Auto Care"
              required
            />
          </label>
          <label>
            Owner name
            <input
              value={form.ownerName}
              onChange={(event) =>
                setForm((x) => ({ ...x, ownerName: event.target.value }))
              }
              placeholder="Alex Morgan"
              required
            />
          </label>
          <label>
            Owner email
            <input
              type="email"
              value={form.email}
              onChange={(event) =>
                setForm((x) => ({ ...x, email: event.target.value }))
              }
              placeholder="owner@autoshop.com"
              required
            />
          </label>
          <button disabled={sending}>
            {sending ? "Sending…" : "Send onboarding invite"}
          </button>
        </form>
        {notice && <div className="notice">{notice}</div>}
        <section className="panel shops">
          {clients.map((x) => {
            const c = completion(x.onboardingData),
              invite = x.onboardingInviteError
                ? "Invite needs attention"
                : x.onboardingInvitedAt
                  ? "Invite sent"
                  : "Invite not sent";
            return (
              <article key={x.id}>
                <div>
                  <b>
                    {x.businessName ||
                      x.onboardingData?.business?.dbaName ||
                      "Untitled shop"}
                  </b>
                  <span>
                    {x.ownerName || "Owner not set"} •{" "}
                    {x.email || "Owner email needed"} • {invite} • {c.percent}%
                    complete
                  </span>
                </div>
                <div>
                  <button
                    className="secondary"
                    disabled={sending || !x.email}
                    onClick={() => resend(x.id)}
                  >
                    Resend invite
                  </button>
                  <button onClick={() => setSelected(x)}>Open</button>
                </div>
              </article>
            );
          })}
          {!clients.length && (
            <p>No shops yet. Send the first onboarding invite above.</p>
          )}
        </section>
      </main>
    </div>
  );
}
function ChangePin({ token, client, done }) {
  const [p, setP] = useState(""),
    [q, setQ] = useState(""),
    [e, setE] = useState("");
  async function go(x) {
    x.preventDefault();
    if (p.length < 6) return setE("Use at least 6 digits.");
    if (p !== q) return setE("PINs do not match.");
    const r = await call(`/api/client/${client.id}/pin`, {
        token,
        method: "PUT",
        body: { newPin: p },
      }),
      d = await r.json();
    r.ok ? done() : setE(d.error);
  }
  return (
    <main className="login">
      <section>
        <Logo />
        <p className="kicker">First sign-in</p>
        <h1>Choose a private PIN.</h1>
        <form onSubmit={go}>
          <label>New PIN</label>
          <input
            className="pin"
            value={p}
            onChange={(e) =>
              setP(e.target.value.replace(/\D/g, "").slice(0, 10))
            }
          />
          <label>Confirm PIN</label>
          <input
            className="pin"
            value={q}
            onChange={(e) =>
              setQ(e.target.value.replace(/\D/g, "").slice(0, 10))
            }
          />
          {e && <small className="error">{e}</small>}
          <button>Save PIN</button>
        </form>
      </section>
    </main>
  );
}
export default function App() {
  const [a, setA] = useState(),
    id = new URLSearchParams(location.search).get("id");
  if (!a) return <Login id={id} onAuth={setA} />;
  if (a.role === "admin")
    return <Admin token={a.token} logout={() => setA()} />;
  if (a.client.mustChangePIN)
    return (
      <ChangePin
        token={a.token}
        client={a.client}
        done={() =>
          setA({ ...a, client: { ...a.client, mustChangePIN: false } })
        }
      />
    );
  return <Workspace initial={a.client} token={a.token} onExit={() => setA()} />;
}
